import React, { memo, useCallback, useState } from 'react';
import './MessageReactionBar.css';

/** Linha principal (estilo WhatsApp) */
const QUICK_REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const;

/**
 * Reações extras (curadas) — mesma linguagem visual da pílula, sem lib externa.
 * Exclui duplicatas dos primários.
 */
const EXTENDED_REACTION_EMOJIS: string[] = [
  '😍',
  '🤣',
  '😊',
  '😉',
  '😎',
  '🤔',
  '😭',
  '😡',
  '🤯',
  '👏',
  '🙌',
  '💪',
  '🔥',
  '✨',
  '💯',
  '🎉',
  '🥳',
  '🤝',
  '🤗',
  '🫶',
  '😘',
  '🥰',
  '😤',
  '😅',
  '🙄',
  '😏',
  '🥹',
  '😴',
  '🤪',
  '😇',
  '🤠',
  '🥶',
  '🤧',
  '😷',
  '✌️',
  '👌',
  '🤌',
  '👋',
  '☺️',
  '😌',
  '🫂',
  '🤷',
  '🤷‍♂️',
  '🤷‍♀️',
  '👀',
  '🤑',
  '🤐',
  '🫡',
  '🤮',
  '🤡',
  '😬',
  '😳',
  '🥺',
  '😈',
  '👻',
  '💔',
  '❤️‍🔥',
  '💙',
  '💚',
  '💛',
  '🖤',
  '🤍',
  '💋',
  '🌹',
  '☀️',
  '⭐',
  '🌙',
  '⚡',
  '🎯',
  '🏆',
  '🎁',
  '🎵',
  '🍕',
  '☕',
  '🍻',
  '✅',
  '❌',
  '⚠️',
  '🙏🏻',
  '🙏🏽',
  '🙏🏿'
];

const quickSet = new Set<string>(QUICK_REACTION_EMOJIS);

/** Sem repetir primários nem duplicatas na lista */
const EXTRA_EMOJIS: string[] = (() => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const e of EXTENDED_REACTION_EMOJIS) {
    if (quickSet.has(e) || seen.has(e)) continue;
    seen.add(e);
    out.push(e);
  }
  return out;
})();

export interface MessageReactionBarProps {
  onReact: (emoji: string) => void;
  onRemoveReaction?: () => void;
  variant?: 'web' | 'mobile';
}

const MessageReactionBar: React.FC<MessageReactionBarProps> = ({
  onReact,
  onRemoveReaction,
  variant = 'web',
}) => {
  const [expanded, setExpanded] = useState(false);

  const gridClass =
    variant === 'mobile'
      ? 'message-reaction-bar-extra-grid message-reaction-bar-extra-grid--mobile'
      : 'message-reaction-bar-extra-grid';

  const handlePick = useCallback(
    (emoji: string) => {
      onReact(emoji);
      setExpanded(false);
    },
    [onReact]
  );

  const rootClass =
    variant === 'mobile'
      ? 'message-reaction-bar message-reaction-bar--mobile'
      : 'message-reaction-bar';

  return (
    <div className={rootClass}>
      <div className="message-reaction-bar-row">
        <div className="message-reaction-bar-pill" role="group" aria-label="Reações rápidas">
          {QUICK_REACTION_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="message-reaction-bar-emoji"
              title={emoji}
              onClick={(e) => {
                e.stopPropagation();
                handlePick(emoji);
              }}
            >
              {emoji}
            </button>
          ))}
          <button
            type="button"
            className={`message-reaction-bar-expand ${expanded ? 'message-reaction-bar-expand--open' : ''}`}
            aria-expanded={expanded}
            aria-label={expanded ? 'Fechar mais reações' : 'Mais reações'}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
          >
            +
          </button>
        </div>
      </div>

      {expanded && (
        <div
          className="message-reaction-bar-extra"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div
            className="message-reaction-bar-extra-scroll"
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <div className={gridClass} aria-label="Mais reações">
              {EXTRA_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="message-reaction-bar-extra-emoji"
                  title={emoji}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePick(emoji);
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {onRemoveReaction && (
        <button
          type="button"
          className="message-reaction-bar-remove"
          onClick={(e) => {
            e.stopPropagation();
            onRemoveReaction();
          }}
        >
          Remover reação
        </button>
      )}
    </div>
  );
};

export default memo(MessageReactionBar);
